"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { GoodLuckCoreDTOsNextMatchResponse } from "@/shared/api/generated-kubb/types";
import { api } from "@/shared/lib/api";
import { useOnNotification } from "@/shared/providers/notification-connection";

import {
  ACTIVE_MATCH_SIGNAL_EVENT_TYPES,
  ME_SAFETY_REFETCH_MS,
} from "./use-active-match";

/**
 * Ближайший назначенный матч пользователя — для отсчёта в шапке.
 *
 * KAN-935. Раньше эта ручка жила сырым `fetch`-циклом прямо в компоненте шапки
 * (`widgets/header/header-up-next.tsx`): два запроса каждые 30 секунд, на каждой
 * странице, у каждого залогиненного, **без оглядки на видимость вкладки**. По
 * замеру прод-логов это выходило дороже самой страницы турнира — 240 запросов
 * в час с одной вкладки против 180.
 *
 * Здесь ровно та же политика, что у `useActiveMatch`: пуш как быстрый путь
 * (уведомления `match_reminder` / `predraft_ready` приходят заранее), редкий
 * страховочный интервал как гарантия, и полное молчание в скрытой вкладке.
 */

/** Ответ бэкенда после проверки: все поля DTO обязательны (KAN-1182 — тип из Kubb). */
export type NextMatch = {
  [K in keyof GoodLuckCoreDTOsNextMatchResponse]-?: NonNullable<
    GoodLuckCoreDTOsNextMatchResponse[K]
  >;
};

export const NEXT_MATCH_QUERY_KEY = ["me", "next-match"] as const;

export function useNextMatch(enabled: boolean) {
  const queryClient = useQueryClient();

  useOnNotification(
    useCallback(
      (notification) => {
        if (!enabled) return;
        if (!notification.eventType) return;
        if (!ACTIVE_MATCH_SIGNAL_EVENT_TYPES.has(notification.eventType)) return;
        void queryClient.invalidateQueries({ queryKey: NEXT_MATCH_QUERY_KEY });
      },
      [enabled, queryClient],
    ),
  );

  return useQuery<NextMatch | null>({
    queryKey: NEXT_MATCH_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<GoodLuckCoreDTOsNextMatchResponse | null>("/me/next-match");
      // 204 без тела означает «матч не назначен» — это не ошибка
      // (api-клиент отдаёт его как null, KAN-1180).
      const data = res.data;
      if (
        !data?.matchId?.trim() ||
        !data.tournamentId?.trim() ||
        !data.scheduledAtUtc ||
        !Number.isFinite(Date.parse(data.scheduledAtUtc))
      ) {
        if (data == null) return null;
        throw new Error("Invalid next match response");
      }
      return {
        matchId: data.matchId,
        tournamentId: data.tournamentId,
        scheduledAtUtc: data.scheduledAtUtc,
      };
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: ME_SAFETY_REFETCH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
